/* =====================================================================
 * GuideToc 스타일 — 사용자 가이드 왼쪽 목차 패널.
 *
 * 패널 골격(폭 / 헤더 높이 / 접힘 애니메이션)은 통합 에이전트의 최근 항목
 * 패널(`WorkspaceListSidebar`)과 같은 토큰을 쓴다. 두 패널이 같은 자리에서
 * 교대로 뜨므로 치수가 조금만 달라도 화면이 덜컹거린다. 숫자를 복사하지 말고
 * 반드시 같은 토큰을 참조할 것.
 *
 * 항목 행은 메인 사이드바의 모드 항목과 같은 모양(아이콘 칩 + 라벨, 선택 시
 * 흰 카드)을 따른다 — 가이드 목차가 사이드바를 그대로 비추는 구조라, 생김새도
 * 같아야 "사이드바의 그 메뉴 설명" 으로 바로 읽힌다.
 * ===================================================================== */

import styled, { css } from 'styled-components';

/* 접기/펼치기 골격은 `WorkspaceModeLayout` 의 MobileOverlay / ListPane /
 * ListPaneInner 를 그대로 옮긴 것. 통합 에이전트와 같은 폭에서 같은 속도로
 * 접혀야 두 화면을 오갈 때 같은 패널처럼 읽힌다. */
export const MobileOverlay = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: ${({ theme }) => theme.colors.bgOverlay};
    z-index: 30;
  }
`;

export const TocPane = styled.aside<{ $open: boolean }>`
  width: ${({ $open, theme }) => ($open ? theme.dm.listWidth : '0')};
  height: 100%;
  min-height: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition: width ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    width: 280px;
    z-index: 31;
    transition: transform ${({ theme }) => theme.transitions.normal};
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  }
`;

export const TocPaneInner = styled.div<{ $open: boolean }>`
  min-width: ${({ theme }) => theme.dm.listWidth};
  width: ${({ theme }) => theme.dm.listWidth};
  height: 100%;
  display: flex;
  flex-direction: column;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 280px;
    width: 280px;
    opacity: 1;
    pointer-events: auto;
  }
`;

/* `WorkspaceListSidebar.styles.ts` 의 Container. */
export const Toc = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid
    ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(23,23,23,0.82)' : 'rgba(249,249,249,0.78)'};
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
`;

/* 최근 항목 패널의 Header. 두 패널을 오갈 때 제목 baseline 이 같은 높이에
 * 앉아야 해서 headerHeight 를 그대로 쓴다. */
export const TocHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: ${({ theme }) => theme.sidebar.headerHeight};
  padding: ${({ theme }) => theme.sidebar.headerPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.sidebarMobile.headerHeight};
    padding: ${({ theme }) => theme.sidebarMobile.headerPad};
  }
`;

/* 최근 항목 패널의 HeaderLeft. */
export const TocHeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const TocHeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

/* 최근 항목 패널의 MainSidebarOpenBtn. */
export const TocIconButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
    background: transparent;
  }
`;

/* 최근 항목 패널의 SidebarToggleBtn — 목차를 접는 버튼. */
export const TocToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.56)'};
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,20,24,0.42)' : 'rgba(255,255,255,0.38)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 10px 24px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)'
      : '0 10px 24px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.48)'};
  backdrop-filter: blur(18px) saturate(170%);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast},
    border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(48,51,58,0.68)' : 'rgba(255,255,255,0.52)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }
`;

/* 최근 항목 패널의 Title. */
export const TocTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.8125rem;
  }
`;

/* 최근 항목 패널의 List. 하단 여백은 마지막 항목이 뷰포트 끝에 붙지 않도록
 * listPad 에 얹는다 — 최근 항목 목록은 무한 스크롤이라 이 여백이 없다. */
export const TocScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.dm.listPad};
  padding-bottom: 16px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.dmMobile.listPad};
    padding-bottom: 12px;
  }
`;

/* 메인 사이드바의 섹션 제목("워크스페이스" / "관리")과 같은 회색 톤. 파란 제목은
 * 항목보다 눈에 먼저 들어와 목차가 제목 위주로 읽혔다. 대신 섹션 사이 간격을
 * 넉넉히 둬서 경계를 만든다. */
export const TocSectionHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  padding: 6px 8px 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:first-child {
    margin-top: 2px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 8px 8px 4px;
    font-size: 0.6875rem;
  }
`;

export const TocSectionChevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

/* 가이드 검색. 항목 이름과 문서 안 절 제목을 함께 찾는다. */
export const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 2px 12px 6px;
  padding: 0 8px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textMuted};
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.brand};
  }

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.8125rem;
    color: ${({ theme }) => theme.colors.textPrimary};

    &::placeholder {
      color: ${({ theme }) => theme.colors.textMuted};
    }

    /* 브라우저 기본 지우기 버튼을 숨긴다 — 같은 역할의 SearchClear 가 따로 있다. */
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
`;

export const SearchClear = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const EmptyResult = styled.p`
  margin: 16px 12px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* 메인 사이드바 `ModeItem` 과 같은 자세: 선택된 항목은 흰 카드로 떠오른다.
 * 오른쪽 문서를 결정하는 선택이라, 다른 행에 마우스를 올려도 어느 문서를
 * 보고 있는지 잃지 않도록 호버(옅은 회색)와 확실히 다른 모양을 쓴다. */
export const TocItem = styled.button<{ $active: boolean; $withToggle?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
  padding: 6px ${({ $withToggle }) => ($withToggle ? '34px' : '8px')} 6px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  cursor: pointer;
  text-align: left;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.sidebar.historyFontSize};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.colors.bgPrimary};
      font-weight: 600;
      box-shadow: ${theme.mode === 'dark'
        ? '0 1px 2px rgba(0,0,0,0.4)'
        : '0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.04)'};

      &:hover {
        background: ${theme.colors.bgPrimary};
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.sidebarMobile.historyFontSize};
  }
`;

export const TocItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* 섹션에 속한 항목 묶음. 섹션 제목 글자 시작선 아래로 옅은 세로선을 긋고
 * 항목을 그 안쪽에 둔다 — 탐색기의 폴더 · 파일처럼 소속이 한눈에 보인다.
 * 제목 없는 섹션(관리자 콘솔 단독 항목)은 들이지 않는다. */
export const TocGroup = styled.div<{ $nested: boolean }>`
  ${({ $nested, theme }) =>
    $nested &&
    css`
      margin: 2px 0 0 12px;
      padding-left: 6px;
      border-left: 1px solid ${theme.colors.borderLight};
    `}
`;

/* 항목 버튼 한 줄. 펼침 화살표는 오른쪽 끝에 겹쳐 둔다. */
export const TocRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 1px 0;
`;

export const TocExpandButton = styled.button<{ $open: boolean }>`
  position: absolute;
  right: 6px;
  top: 50%;
  margin-top: -11px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  svg {
    transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
    transition: transform ${({ theme }) => theme.transitions.fast};
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 1px;
  }
`;

/* 아직 문서가 없는 항목 표시. 눌러도 되지만 기대치를 미리 맞춘다. */
export const TocItemBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* 펼친 문서의 절 목록 — 문서에 "매달린" 가지처럼 보이게 한다.
 * 세로선은 위 항목 이름의 시작선 근처(14px)에서 내려오고,
 * 읽고 있는 절은 그 선 위에 파란 막대로 겹쳐 표시한다.
 * 본문 오른쪽 목차는 넓은 화면에서만 보이므로 1440px 폭에서도 위치를 잡게 여기에 둔다. */
export const HeadingList = styled.ul`
  position: relative;
  list-style: none;
  margin: 0 0 8px 14px;
  padding: 2px 0 2px 10px;
  border-left: 1px solid ${({ theme }) => theme.colors.border};
`;

export const HeadingLink = styled.button<{ $active: boolean }>`
  position: relative;
  width: 100%;
  display: block;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  font-size: 0.78125rem;
  line-height: 1.45;
  cursor: pointer;
  color: ${({ $active, theme }) => ($active ? theme.colors.brand : theme.colors.textSecondary)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  word-break: keep-all;
  transition:
    background ${({ theme }) => theme.transitions.fast},
    color ${({ theme }) => theme.transitions.fast};

  /* 가지선 위의 현재 위치 표시. 목록의 세로선(왼쪽 14px 바깥)에 정확히 겹친다. */
  &::before {
    content: '';
    position: absolute;
    left: -11px;
    top: 4px;
    bottom: 4px;
    width: 2px;
    border-radius: 2px;
    background: ${({ $active, theme }) => ($active ? theme.colors.brand : 'transparent')};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ $active, theme }) => ($active ? theme.colors.brand : theme.colors.textPrimary)};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 1px;
  }
`;

export const HeadingMatch = styled.mark`
  background: transparent;
  color: ${({ theme }) => theme.colors.brand};
  font-weight: 600;
`;
